import { CustomTable } from 'common/components/table/customTable';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { getWebhooksColumnDefs } from './webhooks-column-defs';
import { Webhook } from '../webhooks.models';
import { WebhookStatus } from '../webhook-status';
import { ModalSimple } from 'features/modal/modal-simple';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import {
  onWebhookToBeDeletedChanged,
  selectWebhookToBeDeleted
} from './webhooks-table.slice';
import { useUpdateWebhookMutation } from 'app/apis/commandApi';
import { onSuccess } from 'features/toast/toast.slice';
import { useDispatch, useSelector } from 'react-redux';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useMemo } from 'react';

interface WebhooksTableProps {
  webhooks: Webhook[];
}

export default function WebhooksTable({ webhooks }: WebhooksTableProps) {
  const dispatch = useDispatch();

  const webhookToBeDeleted = useSelector(selectWebhookToBeDeleted);

  const [
    updateWebhookStatusToDeletedMutation,
    updateWebhookStatusToDeletedMetadata
  ] = useUpdateWebhookMutation();

  const onDeleteWebhookConfirmed = () => {
    const disabledWebhook: Webhook = {
      ...webhookToBeDeleted!,
      status: WebhookStatus.DELETED
    };

    void updateWebhookStatusToDeletedMutation(disabledWebhook)
      .unwrap()
      .then(() => {
        dispatch(
          onSuccess({
            title: 'Webhook Deleted',
            description: `The Webhook '${
              webhookToBeDeleted!.name
            }' has been deleted successfully.`
          })
        );

        dispatch(onWebhookToBeDeletedChanged());
      })
      .catch(() => dispatch(onWebhookToBeDeletedChanged()));
  };

  const columnDefs = getWebhooksColumnDefs();

  const enabledWebhooks = useMemo(
    () =>
      webhooks.filter((webhook) => webhook.status !== WebhookStatus.DELETED),
    [webhooks]
  );

  const webhooksTable = useReactTable({
    data: enabledWebhooks,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  });

  return (
    <>
      <ModalSimple
        isOpen={webhookToBeDeleted !== undefined}
        theme={ButtonTheme.ERROR}
        title="Delete Webhook"
        description={`Are you sure you want to delete webhook '${webhookToBeDeleted?.name}'`}
        confirmButtonText="Delete Webhook"
        isLoading={
          updateWebhookStatusToDeletedMetadata.status === QueryStatus.pending
        }
        onConfirmModal={() => onDeleteWebhookConfirmed()}
        onCancelModal={() => dispatch(onWebhookToBeDeletedChanged())}
      />

      <CustomTable
        key={`webhooks-table`}
        componentId={'webhooks-table'}
        table={webhooksTable}
        cellClasses=""
      />
    </>
  );
}
